export const DEFAULT_TREASURE_MAP_FILTERS = {
  query: '',
  activeCategory: 'all',
  nearbyOnly: false,
  lootableOnly: false,
  maxDistanceMeters: 100,
  layers: {},
};

function normalizeQuery(value) {
  return String(value || '').trim().toLowerCase();
}

function matchesQuery(item, query) {
  if (!query) {
    return true;
  }

  const haystack = [
    item?.title,
    item?.name,
    item?.description,
    item?.location_desc,
    item?.beschreibung,
    item?.category,
    item?.categoryLabel,
    item?.author_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
}

function matchesCategory(item, activeCategory) {
  if (!activeCategory || activeCategory === 'all') {
    return true;
  }

  return item?.categoryKey === activeCategory || item?.category === activeCategory || item?.categoryLabel === activeCategory;
}

function matchesLayers(item, layers) {
  if (!layers || typeof layers !== 'object' || Object.keys(layers).length === 0) {
    return true;
  }

  if (item?.sourceType && Object.prototype.hasOwnProperty.call(layers, item.sourceType) && layers[item.sourceType] === false) {
    return false;
  }

  if (item?.categoryKey && Object.prototype.hasOwnProperty.call(layers, item.categoryKey) && layers[item.categoryKey] === false) {
    return false;
  }

  return true;
}

function matchesNearby(item, nearbyOnly, maxDistanceMeters) {
  if (!nearbyOnly) {
    return true;
  }

  return Number.isFinite(item?.distanceMeters) && item.distanceMeters <= maxDistanceMeters;
}

function matchesLootable(item, lootableOnly) {
  if (!lootableOnly) {
    return true;
  }

  return item?.lootableNow === true;
}

export function filterTreasureMapItems(items = [], filters = {}) {
  const mergedFilters = {
    ...DEFAULT_TREASURE_MAP_FILTERS,
    ...(filters || {}),
  };

  const query = normalizeQuery(mergedFilters.query);

  return (Array.isArray(items) ? items : []).filter((item) => {
    return (
      matchesQuery(item, query) &&
      matchesCategory(item, mergedFilters.activeCategory) &&
      matchesLayers(item, mergedFilters.layers) &&
      matchesNearby(item, mergedFilters.nearbyOnly, mergedFilters.maxDistanceMeters) &&
      matchesLootable(item, mergedFilters.lootableOnly)
    );
  });
}

export function splitTreasureMapItemsBySource(items = []) {
  return (Array.isArray(items) ? items : []).reduce(
    (accumulator, item) => {
      if (item?.sourceType === 'donation') {
        accumulator.donations.push(item);
      } else if (item?.sourceType === 'bottle_message') {
        accumulator.bottleMessages.push(item);
      } else {
        accumulator.locations.push(item);
      }
      return accumulator;
    },
    { locations: [], donations: [], bottleMessages: [] }
  );
}

export function sortTreasureMapItems(items = [], sortMode = 'distance') {
  const safeItems = Array.isArray(items) ? [...items] : [];

  if (sortMode === 'title') {
    return safeItems.sort((a, b) => String(a?.title || '').localeCompare(String(b?.title || ''), 'de'));
  }

  return safeItems.sort((a, b) => {
    const distanceA = Number.isFinite(a?.distanceMeters) ? a.distanceMeters : Number.MAX_SAFE_INTEGER;
    const distanceB = Number.isFinite(b?.distanceMeters) ? b.distanceMeters : Number.MAX_SAFE_INTEGER;
    return distanceA - distanceB;
  });
}

export function getTreasureMapActiveFilterCount(filters = {}) {
  const mergedFilters = {
    ...DEFAULT_TREASURE_MAP_FILTERS,
    ...(filters || {}),
  };

  let count = 0;

  if (normalizeQuery(mergedFilters.query)) count += 1;
  if (mergedFilters.activeCategory && mergedFilters.activeCategory !== 'all') count += 1;
  if (mergedFilters.nearbyOnly) count += 1;
  if (mergedFilters.lootableOnly) count += 1;
  if (Number(mergedFilters.maxDistanceMeters) !== Number(DEFAULT_TREASURE_MAP_FILTERS.maxDistanceMeters)) count += 1;

  count += Object.values(mergedFilters.layers || {}).filter((value) => value === false).length;

  return count;
}
